import { FilterOptionDto } from '@act/shared/data-transfer-objects';
import { useRef } from 'react';

export type AutocompleteSelection = FilterOptionDto[] | FilterOptionDto | null;

/**
 * Compares two autocomplete selections by option value rather than by reference.
 *
 * @param previous - The selection returned on the last render.
 * @param next - The selection derived on this render.
 * @returns Whether both selections represent the same chosen options.
 */
const isSameSelection = (
  previous: AutocompleteSelection,
  next: AutocompleteSelection
): boolean => {
  if (previous === next) {
    return true;
  }

  if (Array.isArray(previous) && Array.isArray(next)) {
    return (
      previous.length === next.length &&
      previous.every((option, index) => option?.value === next[index]?.value)
    );
  }

  if (Array.isArray(previous) || Array.isArray(next)) {
    return false;
  }

  return previous?.value === next?.value;
};

/**
 * Keeps an autocomplete's selected-option reference stable while the chosen
 * options are unchanged.
 *
 * MUI's `useAutocomplete` compares the incoming `value` prop against the
 * previous render by reference. A new array on every render reads as a value
 * change, which makes MUI reset the input and discard whatever the user is
 * typing. Returning the previous reference keeps that reset from firing on
 * unrelated parent re-renders.
 *
 * @param selection - The selection derived from the form value and options.
 * @returns A reference-stable equivalent of the derived selection.
 */
export const useStableSelectedOptions = (
  selection: AutocompleteSelection
): AutocompleteSelection => {
  const stableSelection = useRef<AutocompleteSelection>(selection);

  if (!isSameSelection(stableSelection.current, selection)) {
    stableSelection.current = selection;
  }

  return stableSelection.current;
};
